import { CompanyRequestError } from '@/company/api';
import { dateLabel } from '../primitives';
import connectionsCopy, { type ConnectionsCopy } from '@/i18n/ns/connections';
import { formatCopy } from '@/i18n/ns';
import { formatCount } from '@/i18n/format';
import { AgentResponseError } from './api';
import type { AgentAddress, KnowledgeClientGrant } from './types';

// DES1 HC-08: these helpers stay plain functions (not hooks). A component passes the
// `connections` copy and the interface locale it already has; without them the wording is the
// English source, which is also what the setup prompt for an external agent uses.
const EN: ConnectionsCopy = connectionsCopy.en;

/** A date in the reader's locale, for the lines below. */
function shortDate(value: string, locale?: string) {
  const at = new Date(value);
  if (!Number.isFinite(at.getTime())) return dateLabel(value, locale);
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(at);
  } catch {
    return dateLabel(value, locale);
  }
}

function relativeFormat(locale: string) {
  try {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  } catch {
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  }
}

/** "just now", "5 minutes ago", "yesterday", or a date for anything older than a week. */
export function relativeTime(
  value: string | null,
  now = Date.now(),
  copy: ConnectionsCopy = EN,
  locale = 'en',
): string {
  if (!value) return copy.agentTimeNotYet;
  const at = Date.parse(value);
  if (!Number.isFinite(at)) return copy.agentTimeUnknown;
  const seconds = Math.round((now - at) / 1000);
  if (seconds < 45) return copy.agentTimeJustNow;
  const format = relativeFormat(locale);
  if (seconds < 3600) return format.format(-Math.max(1, Math.round(seconds / 60)), 'minute');
  if (seconds < 86_400) return format.format(-Math.round(seconds / 3600), 'hour');
  if (seconds < 7 * 86_400) return format.format(-Math.round(seconds / 86_400), 'day');
  return formatCopy(copy.agentTimeOnDate, { date: shortDate(value, locale) });
}

// Error codes the agent-access routes send that this page can explain
// specifically. Anything else falls back to the caller's plain description of
// what did not happen.
const messages = (copy: ConnectionsCopy): Record<string, string> => ({
  agent_access_not_found: copy.agentErrorNotFound,
  agent_access_disabled: copy.connectingAgentsOff,
  // PATCH asked for a scope this workspace does not offer right now.
  scope_unavailable: copy.agentErrorScopeUnavailable,
  too_many_access_keys: copy.agentErrorTooManyKeys,
  invalid_agent_access_input: copy.agentErrorInvalidInput,
});

/**
 * Whether a change may or may not have happened: the response never arrived,
 * could not be read, or came from a failing server or gateway (5xx) after the
 * request left. The page then says it could not confirm and re-reads; it never
 * reports "did not happen" and never resends on its own.
 */
export const isLostResponse = (error: unknown) =>
  error instanceof CompanyRequestError
    ? error.status >= 500
    : !(error instanceof AgentResponseError);

export function agentErrorMessage(
  error: unknown,
  fallback: string,
  disabledText?: string,
  copy: ConnectionsCopy = EN,
): string {
  if (error instanceof CompanyRequestError) {
    if (error.code === 'agent_access_disabled' && disabledText) return disabledText;
    return messages(copy)[error.code] ?? (error.status === 403 ? error.message : fallback);
  }
  if (error instanceof AgentResponseError) return copy.agentErrorUnreadable;
  return fallback;
}

/** The sample question in the English setup prompt; the page itself shows `setupSampleQuestion`. */
export const SAMPLE_QUESTION = EN.setupSampleQuestion;

/** A day in the reader's locale ("23 Sep 2026"), or that none was recorded. */
export function dayLabel(value: string | null, copy: ConnectionsCopy = EN, locale?: string) {
  return value && Number.isFinite(Date.parse(value))
    ? shortDate(value, locale)
    : copy.agentNotRecorded;
}

/**
 * The grant with its names filled in: an agent that reported no name, or a
 * grant without its company's name, reads in the interface language.
 */
export function withNames(grant: KnowledgeClientGrant, copy: ConnectionsCopy = EN) {
  return grant.clientName && grant.companyName
    ? grant
    : {
        ...grant,
        clientName: grant.clientName || copy.agentUnnamed,
        companyName: grant.companyName || copy.agentCompanyFallback,
      };
}

/** "23 Sep 2026, 14:05" in the reader's locale. */
export function dateTimeLabel(
  value: string | null,
  copy: ConnectionsCopy = EN,
  locale?: string,
): string {
  const at = value ? Date.parse(value) : NaN;
  if (!Number.isFinite(at)) return copy.agentNotRecorded;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(at);
  } catch {
    return new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(at);
  }
}

/* ------------------------------------------------------------------ status */
// One wording for a connected agent, shared by Connections and the chat card.

/**
 * 'stale': made for an earlier MCP address, so every request is refused and
 * the agent has to connect again; never ready, whatever it did before.
 */
export type GrantState = 'ready' | 'waiting' | 'stale';

export function grantState(grant: KnowledgeClientGrant): GrantState {
  if (!grant.addressMatches) return 'stale';
  return grant.status === 'ready' ? 'ready' : 'waiting';
}

/** How the agent reaches Poneva. */
export const methodLabel = (grant: KnowledgeClientGrant, copy: ConnectionsCopy = EN) =>
  grant.kind === 'access_key' ? copy.agentMethodKey : copy.agentMethodSignedIn;

/** The short state: "Ready", "Authorized — waiting for first read", … */
export function stateLabel(grant: KnowledgeClientGrant, copy: ConnectionsCopy = EN): string {
  const state = grantState(grant);
  if (state === 'stale') return copy.agentStateReconnect;
  if (state === 'ready') return copy.agentStateReady;
  return grant.kind === 'access_key' ? copy.agentStateKeyUnused : copy.agentStateWaiting;
}

/** "Last used 5 minutes ago", for a ready agent that has read something. */
export function lastUsedLabel(
  grant: KnowledgeClientGrant,
  now: number,
  copy: ConnectionsCopy = EN,
  locale = 'en',
): string | null {
  const at = grant.lastUsedAt ?? grant.firstReadAt;
  return grantState(grant) === 'ready' && at
    ? formatCopy(copy.agentLastUsed, { when: relativeTime(at, now, copy, locale) })
    : null;
}

/** "Ready · Last used 5 minutes ago": the state, then its last use. */
export const statusLine = (
  grant: KnowledgeClientGrant,
  now: number,
  copy: ConnectionsCopy = EN,
  locale = 'en',
) => [stateLabel(grant, copy), lastUsedLabel(grant, now, copy, locale)].filter(Boolean).join(' · ');

/** "Access key · expires 24 Oct 2026": how it connects and until when. */
export function methodLine(
  grant: KnowledgeClientGrant,
  copy: ConnectionsCopy = EN,
  locale?: string,
): string {
  // An unknown end (null) is left unsaid rather than shown as never.
  if (!grant.expiresAt) return methodLabel(grant, copy);
  const date = shortDate(grant.expiresAt, locale);
  return formatCopy(grant.kind === 'access_key' ? copy.agentKeyExpires : copy.agentSignInEnds, {
    date,
  });
}

/** Agents Poneva still serves: an agent on an old address is refused. */
export const connectedCount = (clients: KnowledgeClientGrant[]) =>
  clients.filter((grant) => grantState(grant) !== 'stale').length;

/** "2 agents connected · 1 waiting · 1 to reconnect". */
export function agentsSummary(
  clients: KnowledgeClientGrant[],
  copy: ConnectionsCopy = EN,
  locale = 'en',
): string {
  if (clients.length === 0) return copy.agentsNone;
  const states = clients.map(grantState);
  const connected = connectedCount(clients);
  const waiting = states.filter((state) => state === 'waiting').length;
  const stale = states.filter((state) => state === 'stale').length;
  return [
    connected ? formatCount(copy.agentsConnectedCount, connected, locale) : '',
    waiting ? formatCount(copy.agentsWaitingCount, waiting, locale) : '',
    stale ? formatCount(copy.agentsReconnectCount, stale, locale) : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * What the server's own address facts mean for an external agent, or null
 * when there is nothing to warn about. A local address is never self-checked.
 */
export function addressWarning(address: AgentAddress, copy: ConnectionsCopy = EN): string | null {
  if (address.localOnly)
    return address.network === 'private' ? copy.addressLocalNetwork : copy.addressLocalComputer;
  return address.reachable === 'no' ? copy.addressUnreachable : null;
}
